/**
 * Calculate the distance between two coordinates using the Haversine formula
 * @param lat1 Latitude of first point
 * @param lon1 Longitude of first point
 * @param lat2 Latitude of second point
 * @param lon2 Longitude of second point
 * @returns Distance in meters
 */
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
};

/**
 * Parse coordinates string (format: "lat,lng") to object
 * @param coordinatesString String in format "lat,lng"
 * @returns Object with lat and lng, or null if invalid
 */
export const parseCoordinates = (
  coordinatesString: string | null | undefined
): { lat: number; lng: number } | null => {
  if (!coordinatesString) return null;

  const parts = coordinatesString.split(",").map((v) => v.trim());
  if (parts.length !== 2) return null;

  const lat = parseFloat(parts[0]);
  const lng = parseFloat(parts[1]);

  if (isNaN(lat) || isNaN(lng)) return null;

  return { lat, lng };
};

/**
 * Get user's current location using browser geolocation API
 * @returns Promise with user's coordinates
 */
export const getUserLocation = (): Promise<{ lat: number; lng: number }> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser. Please use a modern browser with location services."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        // Provide user-friendly error messages based on error code
        let errorMessage = "Failed to get your location. ";
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location permission denied. Please enable location access in your browser settings and try again.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable. Please check your device's location settings.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out. Please try again.";
            break;
          default:
            errorMessage = "An error occurred while getting your location. Please try again.";
            break;
        }
        
        reject(new Error(errorMessage));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });
};

/**

* Open Google Maps with navigation to the specified coordinates
 * This will open the Google Maps app on mobile devices if installed,
 * otherwise it will open the web version
 * @param lat Latitude of destination
 * @param lng Longitude of destination
 * @param address Optional address string for fallback
 */
export const openGoogleMapsNavigation = (
  lat: number,
  lng: number,
  address?: string
): void => {
  // Check if we're on a mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  if (isMobile) {
    // On mobile, try to open Google Maps app first using the app URL scheme
    const appUrl = `google.navigation:q=${lat},${lng}`;
    
    // Try to open the app
    window.location.href = appUrl;
    
    // Fallback: If app doesn't open, use web URL (this will be triggered if app is not installed)
    // Note: This is a best-effort approach. The web URL will open if app URL fails
    setTimeout(() => {
      const webUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
      // Only open web if we're still on the same page (app didn't open)
      if (document.visibilityState === 'visible') {
        window.open(webUrl, '_blank');
      }
    }, 1000);
  } else {
    // On desktop, open web version in new tab
    const webUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(webUrl, '_blank');
  }
};

